import type {
  BuildManifest,
  DwgScanUpload,
  NetworkMode,
  ProjectFacts,
  ProjectType,
  ReviewItem,
  ZoneAttachment,
} from "@plan-forge/templatecreator-contract";
import { isPalmBeachCounty } from "@plan-forge/templatecreator-contract";

export interface ZoneChoice {
  readonly surgeProtector: boolean;
  readonly terminalBlock: boolean;
}

export interface FormState {
  readonly projectName: string;
  readonly district: string;
  readonly address: string;
  readonly projectType: ProjectType;
  readonly networkMode: NetworkMode;
  readonly roomCombineMs700: number;
  readonly roomCombine70v: number;
  readonly twoWaySystemType: "" | "WPA" | "ITC";
  readonly twoWaySystemStrobe: boolean;
  readonly poeSurgeProtectors: boolean;
  readonly safeDongles: boolean;
  readonly cutover: boolean;
  readonly zones: Readonly<Record<string, ZoneChoice>>;
}

export const EMPTY_ZONE: ZoneChoice = { surgeProtector: false, terminalBlock: false };

export const INITIAL_FORM: FormState = {
  projectName: "",
  district: "",
  address: "",
  projectType: "Retrofit",
  networkMode: "Existing",
  roomCombineMs700: 0,
  roomCombine70v: 0,
  twoWaySystemType: "",
  twoWaySystemStrobe: false,
  poeSurgeProtectors: false,
  safeDongles: false,
  cutover: false,
  zones: {},
};

/** New Construction drops are always the base building's scope, so AE Provided cannot stay selected. */
export function withProjectType(form: FormState, projectType: ProjectType): FormState {
  return {
    ...form,
    projectType,
    networkMode: projectType === "New Construction" ? "Existing" : form.networkMode,
  };
}

/** There is no WPA strobe diagram, so choosing WPA clears the strobe. */
export function withTwoWaySystem(form: FormState, twoWaySystemType: FormState["twoWaySystemType"]): FormState {
  return { ...form, twoWaySystemType, twoWaySystemStrobe: twoWaySystemType === "WPA" ? false : form.twoWaySystemStrobe };
}

/** A terminal block only exists on a cutover project, so turning cutover off takes them back off every zone. */
export function withCutover(form: FormState, cutover: boolean): FormState {
  const zones = cutover
    ? form.zones
    : Object.fromEntries(Object.entries(form.zones).map(([zone, choice]) => [zone, { ...choice, terminalBlock: false }]));
  return { ...form, cutover, zones };
}

export function withZone(form: FormState, zone: string, patch: Partial<ZoneChoice>): FormState {
  return { ...form, zones: { ...form.zones, [zone]: { ...(form.zones[zone] ?? EMPTY_ZONE), ...patch } } };
}

export type ZoneAttachmentKind = keyof ZoneChoice;

/** Case-insensitive substring match, so "hall" finds every hallway. A blank query matches everything. */
export function filterZones(zones: readonly string[], query: string): string[] {
  const needle = query.trim().toLowerCase();
  return needle === "" ? [...zones] : zones.filter((zone) => zone.toLowerCase().includes(needle));
}

/** Sets one attachment on every given zone at once, for schools with dozens of zones. */
export function withZones(form: FormState, zones: readonly string[], kind: ZoneAttachmentKind, on: boolean): FormState {
  const next: Record<string, ZoneChoice> = { ...form.zones };
  for (const zone of zones) next[zone] = { ...(next[zone] ?? EMPTY_ZONE), [kind]: on };
  return { ...form, zones: next };
}

export function toFacts(form: FormState, scan: DwgScanUpload | null, sourceDwg: string): ProjectFacts {
  const zoneAttachments: ZoneAttachment[] = [];
  for (const zone of scan?.zoneNames ?? []) {
    const choice = form.zones[zone] ?? EMPTY_ZONE;
    const terminalBlock = form.cutover && choice.terminalBlock;
    if (choice.surgeProtector || terminalBlock) zoneAttachments.push({ zone, surgeProtector: choice.surgeProtector, terminalBlock });
  }
  return {
    projectName: form.projectName.trim(),
    district: form.district.trim(),
    address: form.address.trim(),
    projectType: form.projectType,
    networkMode: form.networkMode,
    selectedSpeakers: [],
    devices: [],
    flags: {},
    orderProducts: [],
    orderProductQuantities: {},
    orderReadOk: false,
    orderReadMessage: "",
    solutions: [],
    manualDiagramQuantities: { F008: Math.max(0, form.roomCombineMs700), F009: Math.max(0, form.roomCombine70v) },
    ...(isPalmBeachCounty(form.district, form.address)
      ? { twoWaySystemType: "", twoWaySystemStrobe: false }
      : { twoWaySystemType: form.twoWaySystemType, twoWaySystemStrobe: form.twoWaySystemStrobe }),
    poeSurgeProtectors: form.poeSurgeProtectors,
    safeDongles: form.safeDongles,
    cutover: form.cutover,
    zoneAttachments,
    sourceDwg,
    detectedBlocks: scan?.blockCounts ?? {},
    dwgScanAvailable: scan !== null,
    volumeControlZones: scan?.volumeControlZones ?? [],
    zoneNames: scan?.zoneNames ?? [],
    generalNotesFound: scan?.generalNotesFound ?? null,
    blankAttributes: scan?.blankAttributes ?? {},
  };
}

const GROUP_ORDER = ["Drawing", "Site Map", "Scope of Work", "Sheet Index", "Risers", "Other"] as const;
export type ReviewGroup = (typeof GROUP_ORDER)[number];

export function reviewGroupLabel(category: string): ReviewGroup {
  if (category === "missing-attributes") return "Drawing";
  if (category === "retrofit-site-map") return "Site Map";
  if (category.startsWith("scope-of-work")) return "Scope of Work";
  if (category === "sheet-index") return "Sheet Index";
  if (category === "riser-update") return "Risers";
  return "Other";
}

export interface KeyedReviewItem extends ReviewItem {
  /** category:message, with #n on a repeat, so a ticked box survives the next build. */
  readonly key: string;
}

export interface ReviewSection {
  readonly group: ReviewGroup;
  readonly items: readonly KeyedReviewItem[];
}

/** Keeps the engine's order and starts a new section each time the group changes, like the desktop panel. */
export function reviewSections(items: readonly ReviewItem[]): ReviewSection[] {
  const seen = new Map<string, number>();
  const sections: { group: ReviewGroup; items: KeyedReviewItem[] }[] = [];
  for (const item of items) {
    const base = `${item.category}:${item.message}`;
    const occurrence = (seen.get(base) ?? 0) + 1;
    seen.set(base, occurrence);
    const keyed: KeyedReviewItem = { ...item, key: occurrence === 1 ? base : `${base}#${occurrence}` };
    const group = reviewGroupLabel(item.category);
    const last = sections[sections.length - 1];
    if (last !== undefined && last.group === group) last.items.push(keyed);
    else sections.push({ group, items: [keyed] });
  }
  return sections;
}

export function reviewProgress(sections: readonly ReviewSection[], completed: ReadonlySet<string>): { done: number; total: number } {
  const keys = sections.flatMap((section) => section.items.map((item) => item.key));
  return { done: keys.filter((key) => completed.has(key)).length, total: keys.length };
}

/** The desktop app's file-name rule: reserved characters become underscores. */
export function packageFileName(projectName: string): string {
  const cleaned = projectName.replace(/[<>:"/\\|?*]+/g, "_").trim().replace(/\.+$/, "");
  return `01 - ${cleaned === "" ? "AE Project" : cleaned}.ae01.json`;
}

const plain = (value: string | undefined): string => (value ?? "").replaceAll("\\P", " / ");

export function summaryText(manifest: BuildManifest, scanMessage: string): string {
  const cover = manifest.cover_attributes;
  const project = manifest.project;
  const grid = manifest.network_riser_config;
  const diagrams = manifest.diagrams.map((item) => item.diagram_id).join(", ") || "None";
  const twoWay =
    project.two_way_system_type === "" ? "None" : `${project.two_way_system_type}${project.two_way_system_type === "ITC" && project.two_way_system_strobe ? " + strobe" : ""}`;
  return [
    "SUMMARY",
    "=======",
    `Project: ${project.project_name}`,
    `Project type: ${project.project_type}`,
    `Solutions: ${plain(cover["SOLUTION_PLAIN"] ?? cover["SOLUTION"]) || "None detected"}`,
    `Cover NAME: ${plain(cover["NAME_PLAIN"] ?? cover["NAME"]) || "Not available"}`,
    `Cover address: ${project.address ? project.address.toUpperCase() : "Not available"}`,
    `Cover source: ${manifest.starter_pages[0]?.source ?? "Not available"}`,
    `03 file device scan: ${project.dwg_scan_available ? "YES" : "NO"} - ${scanMessage}`,
    `Network drops: ${project.network_mode}`,
    `Cutover: ${manifest.cutover ? "YES" : "No"}`,
    `Two-Way System: ${twoWay}`,
    `Pages planned: ${manifest.page_count}`,
    `Diagrams selected: ${manifest.diagrams.length} (${diagrams})`,
    `Diagram grid: ${grid.grid_columns}x${grid.grid_rows} spaces; 1x1 = ${grid.slot_width}in W x ${grid.slot_height}in H; ${grid.gap_x}in gaps`,
    `Network/rack risers: ${manifest.risers.length}`,
    `Review items: ${manifest.review_items.length}`,
  ].join("\n");
}
